import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/tour.css';

/**
 * Runner de tour guidé mono-page (driver.js + thème LogFit `logfit-tour`).
 * Chaque étape peut avoir un hook `before` async (ex: basculer une vue) exécuté
 * avant que driver ne mesure le spotlight — on attend ensuite que l'élément soit
 * présent et peint pour éviter un spotlight sur un DOM transitoire.
 */
export interface PageTourStep {
  /** Sélecteur CSS de la zone à mettre en valeur. Omis = popover centré. */
  element?: string;
  title: string;
  description: string;
  /** Action à jouer avant d'afficher l'étape (ex: setViewMode('grouped')). */
  before?: () => void | Promise<void>;
}

export interface TourLabels {
  next: string;
  prev: string;
  done: string;
  progress: string;
}

export function waitForElement(selector: string | undefined, timeout = 4000): Promise<void> {
  if (!selector) return Promise.resolve();
  return new Promise((resolve) => {
    if (document.querySelector(selector)) return resolve();
    const start = Date.now();
    const id = window.setInterval(() => {
      if (document.querySelector(selector) || Date.now() - start > timeout) {
        window.clearInterval(id);
        resolve();
      }
    }, 80);
  });
}

/** Deux rAF : laisse React peindre (après un changement de vue) avant la mesure du spotlight. */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
}

export function runPageTour(steps: PageTourStep[], labels: TourLabels, onDone?: () => void) {
  const driveSteps: DriveStep[] = steps.map((s) => ({
    element: s.element,
    popover: { title: s.title, description: s.description },
  }));

  const prepare = async (index: number) => {
    const step = steps[index];
    if (!step) return;
    if (step.before) await step.before();
    await waitForElement(step.element);
    await nextPaint();
  };

  const driverObj = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: '#000000',
    overlayOpacity: 0.7,
    stagePadding: 6,
    stageRadius: 8,
    popoverClass: 'logfit-tour',
    progressText: labels.progress,
    nextBtnText: labels.next,
    prevBtnText: labels.prev,
    doneBtnText: labels.done,
    steps: driveSteps,
    onNextClick: async () => {
      const current = driverObj.getActiveIndex() ?? 0;
      const nextIndex = current + 1;
      if (nextIndex >= steps.length) {
        onDone?.();
        driverObj.destroy();
        return;
      }
      await prepare(nextIndex);
      driverObj.moveNext();
    },
    onPrevClick: async () => {
      const current = driverObj.getActiveIndex() ?? 0;
      const prevIndex = current - 1;
      if (prevIndex < 0) return;
      await prepare(prevIndex);
      driverObj.movePrevious();
    },
    onDestroyed: () => onDone?.(),
  });

  prepare(0).then(() => driverObj.drive());
  return driverObj;
}

/**
 * Variante d'un « chapitre » de tour multi-pages. Distingue explicitement la
 * complétion (l'utilisateur a cliqué « Suivant » au-delà de la dernière étape,
 * → `onComplete`) de la fermeture anticipée (croix / Échap, → `onExit`). Le
 * coordinateur multi-pages s'appuie sur cette distinction pour ne naviguer vers
 * le chapitre suivant QUE si le chapitre courant est allé jusqu'au bout.
 */
export function runGuidedChapter(
  steps: PageTourStep[],
  labels: TourLabels,
  opts: { onComplete?: () => void; onExit?: () => void }
) {
  let completed = false;
  const driveSteps: DriveStep[] = steps.map((s) => ({
    element: s.element,
    popover: { title: s.title, description: s.description },
  }));

  const prepare = async (index: number) => {
    const step = steps[index];
    if (!step) return;
    if (step.before) await step.before();
    await waitForElement(step.element);
    await nextPaint();
  };

  const driverObj = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: '#000000',
    overlayOpacity: 0.7,
    stagePadding: 6,
    stageRadius: 8,
    popoverClass: 'logfit-tour',
    progressText: labels.progress,
    nextBtnText: labels.next,
    prevBtnText: labels.prev,
    doneBtnText: labels.done,
    steps: driveSteps,
    onNextClick: async () => {
      const current = driverObj.getActiveIndex() ?? 0;
      const nextIndex = current + 1;
      if (nextIndex >= steps.length) {
        completed = true;
        driverObj.destroy();
        return;
      }
      await prepare(nextIndex);
      driverObj.moveNext();
    },
    onPrevClick: async () => {
      const current = driverObj.getActiveIndex() ?? 0;
      const prevIndex = current - 1;
      if (prevIndex < 0) return;
      await prepare(prevIndex);
      driverObj.movePrevious();
    },
    onDestroyed: () => {
      if (completed) opts.onComplete?.();
      else opts.onExit?.();
    },
  });

  prepare(0).then(() => driverObj.drive());
  return driverObj;
}

// --- Coordinateur de flow guidé multi-pages (Heats → Assignations → Heats) ---
// L'état vit en sessionStorage : il survit à la navigation react-router (qui
// démonte/remonte les pages) mais pas à la fermeture de l'onglet.
const GUIDED_FLOW_KEY = 'logfit_guided_flow';

export interface GuidedFlowState {
  flow: string;
  chapter: number;
}

export function getGuidedFlow(): GuidedFlowState | null {
  try {
    const raw = sessionStorage.getItem(GUIDED_FLOW_KEY);
    return raw ? (JSON.parse(raw) as GuidedFlowState) : null;
  } catch {
    return null;
  }
}

export function setGuidedFlow(state: GuidedFlowState | null) {
  try {
    if (state) sessionStorage.setItem(GUIDED_FLOW_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(GUIDED_FLOW_KEY);
  } catch {
    /* sessionStorage indisponible */
  }
}
